import axios from "axios";
import { cloneDeep } from "lodash";

const pendingRequests = {};

/* -------------------- MODULE-LEVEL FLAG -------------------- */
// Used to prevent duplicate calls for personal bookmarks
let loadingBookmarksForReportIds = new Set();

/* -------------------- INITIAL STATE -------------------- */
const INITIAL_STATE = {
  accessToken: {},
  loading: false,
  error: null,
  pageNames: [],
  personalBookmarks: [],
  sPersonalBookmarks: [],
  bookmarkFilter: "",
};

/* -------------------- ACTION TYPES -------------------- */
const CLEAR_ACCESS_TOKEN = "CLEAR_ACCESS_TOKEN";
const FETCH_ACCESS_TOKEN_BEGIN = "FETCH_ACCESS_TOKEN_BEGIN";
const FETCH_ACCESS_TOKEN_SUCCESS = "FETCH_ACCESS_TOKEN_SUCCESS";
const FETCH_ACCESS_TOKEN_FAILURE = "FETCH_ACCESS_TOKEN_FAILURE";
const REFRESH_ACCESS_TOKEN_SUCCESS = "REFRESH_ACCESS_TOKEN_SUCCESS";
const SET_PERSONAL_BOOKMARKS = "SET_PERSONAL_BOOKMARKS";
const SET_SEARCH_BOOKMARKS = "SET_SEARCH_BOOKMARKS";

/* -------------------- ACTION CREATORS -------------------- */

const clear_access_token = () => {
  return { type: CLEAR_ACCESS_TOKEN, payload: {} };
};

const fetchAccessTokenBegin = () => ({
  type: FETCH_ACCESS_TOKEN_BEGIN,
});
const fetchAccessTokenSuccess = (accessToken) => {
  return { type: FETCH_ACCESS_TOKEN_SUCCESS, payload: accessToken };
};
const fetchAccessTokenError = (error) => ({
  type: FETCH_ACCESS_TOKEN_FAILURE,
  payload: { error },
});
const setSearchBookmarks = (bookmarks) => {
  return { type: SET_SEARCH_BOOKMARKS, payload: bookmarks };
};
const setPersonalBookmarks = (bookmarks) => {
  return { type: SET_PERSONAL_BOOKMARKS, payload: bookmarks };
};

/* -------------------- ASYNC ACTION CREATORS -------------------- */
const TOKEN_REFRESH_LEAD_MS = 5 * 60 * 1000;
const TOKEN_REFRESH_MIN_DELAY_MS = 30 * 1000;
let tokenRefreshTimer = null;
let tokenRefreshContext = null;
let visibilityListenerAdded = false;

// Reuse getToken responses until close to expiry so returning to a report skips the round-trip.
const TOKEN_CACHE_MAX_ENTRIES = 50;
const tokenCache = new Map();

// The server ignores the requested View/Edit mode for reports (permissions decide), so both share
// one entry. Dashboards use the requested mode as the token access level, so they stay separate.
const tokenCacheKey = ({ groupId, reportId, isDashboard, datasetId, mode }) => {
  const keyMode = !isDashboard && mode !== "Create" ? "report" : mode;
  return `${groupId}|${reportId}|${!!isDashboard}|${datasetId}|${keyMode}`;
};

const isTokenUsable = (data) => {
  const expiresAt = data?.expiration ? Date.parse(data.expiration) : NaN;
  return !!data?.token && !Number.isNaN(expiresAt) && expiresAt - Date.now() > TOKEN_REFRESH_LEAD_MS;
};

const cacheToken = (params, data) => {
  // Create-mode tokens are dataset-scoped and short-lived flows; never reuse them.
  if (params.mode === "Create" || !isTokenUsable(data)) return;
  const key = tokenCacheKey(params);
  tokenCache.delete(key);
  tokenCache.set(key, data);
  for (const [k, v] of tokenCache) {
    if (tokenCache.size <= TOKEN_CACHE_MAX_ENTRIES && isTokenUsable(v)) break;
    tokenCache.delete(k);
  }
};

export const clearTokenCache = () => {
  tokenCache.clear();
};

// Report metadata (pages, embedUrl) can change after an edit is saved.
export const invalidateCachedTokens = (reportId) => {
  if (!reportId) return;
  for (const [key, data] of tokenCache) {
    if (key.split("|")[1] === reportId || data?.reportId === reportId) tokenCache.delete(key);
  }
};

const cancelTokenRefresh = () => {
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }
  tokenRefreshContext = null;
};

// background: true keeps timer-driven refreshes from counting as user activity (idle logout).
const postGetToken = (params, background = false) =>
  axios.post(`${process.env.REACT_APP_BIGHORN_SERVER}/api/powerBI/getToken`, params, {
    headers: {
      ClientToken: localStorage.getItem("clientToken"),
    },
    background,
  });

const runTokenRefresh = () => {
  const context = tokenRefreshContext;
  if (!context) return;
  cancelTokenRefresh();
  const { dispatch, getState, params, tokenId } = context;
  postGetToken(params, true)
    .then((response) => {
      // Drop the result if the user moved to another report while the request was in flight.
      if (getState().powerBIReport.accessToken?.tokenId !== tokenId) return;
      cacheToken(params, response.data);
      dispatch({ type: REFRESH_ACCESS_TOKEN_SUCCESS, payload: response.data });
      scheduleTokenRefresh(dispatch, getState, params, response.data);
    })
    .catch(() => {
      if (getState().powerBIReport.accessToken?.tokenId !== tokenId) return;
      tokenRefreshContext = context;
      tokenRefreshTimer = setTimeout(runTokenRefresh, TOKEN_REFRESH_MIN_DELAY_MS);
    });
};

const scheduleTokenRefresh = (dispatch, getState, params, data) => {
  cancelTokenRefresh();
  const expiresAt = data?.expiration ? Date.parse(data.expiration) : NaN;
  if (!data?.tokenId || Number.isNaN(expiresAt)) return;
  tokenRefreshContext = { dispatch, getState, params, tokenId: data.tokenId, expiresAt };
  const delay = Math.max(expiresAt - Date.now() - TOKEN_REFRESH_LEAD_MS, TOKEN_REFRESH_MIN_DELAY_MS);
  tokenRefreshTimer = setTimeout(runTokenRefresh, delay);

  // Background-tab timers are throttled and a sleeping laptop skips them; re-check on return.
  if (!visibilityListenerAdded && typeof document !== "undefined") {
    visibilityListenerAdded = true;
    document.addEventListener("visibilitychange", () => {
      if (
        document.visibilityState === "visible" &&
        tokenRefreshContext &&
        tokenRefreshContext.expiresAt - Date.now() < TOKEN_REFRESH_LEAD_MS
      ) {
        runTokenRefresh();
      }
    });
  }
};

export const getAccessToken = (groupId, reportId, isDashboard = false, datasetId, mode) => {
  return (dispatch, getState) => {
    const dedupKey = `${groupId}-${reportId}-${datasetId}-${mode}`;
    // console.log(`[PBI Performance] getAccessToken called with key: ${dedupKey}`);

    if (pendingRequests[dedupKey]) {
      // console.log(`[PBI Performance] Deduplicating overlapping API request: ${dedupKey}`);
      return pendingRequests[dedupKey];
    }

    const params = { groupId, reportId, isDashboard, datasetId, mode };
    const cached = tokenCache.get(tokenCacheKey(params));
    if (cached && isTokenUsable(cached)) {
      dispatch(fetchAccessTokenSuccess({ ...cached, fromCache: true }));
      scheduleTokenRefresh(dispatch, getState, params, cached);
      return Promise.resolve(cached);
    }

    const requestId = Date.now();
    // console.time(`[PBI Performance] getAccessToken API Request [${requestId}]`);
    // console.log(`[getAccessToken] [${requestId}] Requesting token for Group: ${groupId}, Report: ${reportId}`);
    dispatch(fetchAccessTokenBegin());

    const requestPromise = postGetToken(params)
      .then((response) => {
        delete pendingRequests[dedupKey];
        // console.timeEnd(`[PBI Performance] getAccessToken API Request [${requestId}]`);
        // console.log(`[getAccessToken] [${requestId}] Success! Token received.`);
        cacheToken(params, response.data);
        dispatch(fetchAccessTokenSuccess(response.data));
        scheduleTokenRefresh(dispatch, getState, params, response.data);
        return response.data;
      })
      .catch((error) => {
        delete pendingRequests[dedupKey];
        // console.timeEnd(`[PBI Performance] getAccessToken API Request [${requestId}]`);
        // console.error(`[getAccessToken] [${requestId}] Error:`, error);
        dispatch(fetchAccessTokenError(error));
        throw error;
      });

    pendingRequests[dedupKey] = requestPromise;
    return requestPromise;
  };
};
export const updatePersonalBookmarks = (bookmarks) => {
  return (dispatch) => {
    dispatch(setPersonalBookmarks(bookmarks));
  };
};
// Bounded retry state for getPersonalBookmarks — module scope (not component
// state) since this is a plain action creator, keyed by reportId so retries
// for one report don't consume another's budget.
const MAX_BOOKMARKS_FETCH_RETRIES = 3;
const BOOKMARKS_FETCH_RETRY_DELAY_MS = 3000;
const bookmarksFetchRetryCounts = new Map();

export const getPersonalBookmarks = (reportId) => {
  return (dispatch, getState) => {
    // Prevent duplicate calls for the same reportId
    if (loadingBookmarksForReportIds.has(reportId)) {
      return;
    }
    loadingBookmarksForReportIds.add(reportId);
    axios
      .post(
        `${process.env.REACT_APP_BIGHORN_SERVER}/api/powerBI/getPersonalBookmarksForReport`,
        {
          reportId,
        },
        {
          headers: {
            ClientToken: localStorage.getItem("clientToken"),
          },
        },
      )
      .then((response) => {
        // console.log("GOT PERONSAL BOOKMARKS");

        // console.log(response);
        loadingBookmarksForReportIds.delete(reportId);
        bookmarksFetchRetryCounts.delete(reportId);
        dispatch(setPersonalBookmarks(response.data.pBookmarks));
      })
      .catch((error) => {
        loadingBookmarksForReportIds.delete(reportId);
        // The request can fail even after being accepted by the server (e.g.
        // a response truncated under connection contention) — axios then
        // rejects with no data, and without a retry the bookmarks pane is
        // left empty for the rest of the session with nothing to
        // self-correct it. Retry a few times with a short backoff instead.
        const attempts = bookmarksFetchRetryCounts.get(reportId) || 0;
        if (attempts >= MAX_BOOKMARKS_FETCH_RETRIES) {
          bookmarksFetchRetryCounts.delete(reportId);
          return;
        }
        bookmarksFetchRetryCounts.set(reportId, attempts + 1);
        setTimeout(() => {
          // Only retry if this report is still the one being viewed — the
          // extra delay makes it more likely the user has since moved on,
          // and applying stale bookmarks for a report no longer on screen
          // would be worse than just leaving the retry budget unspent.
          const activeReport = getState().reportList.activeReport;
          if (activeReport && activeReport.id === reportId) {
            dispatch(getPersonalBookmarks(reportId));
          } else {
            bookmarksFetchRetryCounts.delete(reportId);
          }
        }, BOOKMARKS_FETCH_RETRY_DELAY_MS);
      });
  };
};
export const clearAccessToken = () => {
  return (dispatch) => {
    cancelTokenRefresh();
    dispatch(clear_access_token());
  };
};

export const filterBookmarks = (filter, bookmarks) => {
  return (dispatch) => {
    if (filter === "") {
      dispatch(setSearchBookmarks(bookmarks));
    } else {
      let newBookmarks = cloneDeep(bookmarks);

      dispatch(
        setSearchBookmarks(
          newBookmarks.filter((bMark) => {
            return bMark.name.toLowerCase().includes(filter.toLowerCase());
          }),
        ),
      );
    }
  };
};

/* -------------------- REDUCER -------------------- */
export default (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case CLEAR_ACCESS_TOKEN:
      return { ...state, accessToken: action.payload, loading: true };

    case FETCH_ACCESS_TOKEN_BEGIN:
      return { ...state, loading: true, error: null, pageNames: [] };
    case FETCH_ACCESS_TOKEN_SUCCESS:
      let newPN;
      if (action.payload.pageNames !== undefined) {
        newPN = action.payload.pageNames.map((item) => {
          return item.order + "|" + item.Name + "|" + item.displayName;
        });
      }

      return {
        ...state,
        accessToken: action.payload,
        loading: false,
        pageNames: newPN,
      };
    case FETCH_ACCESS_TOKEN_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
        reportConfig: [],
      };
    case REFRESH_ACCESS_TOKEN_SUCCESS:
      // Swap only the token fields; leaving loading/pageNames untouched keeps the embedded report mounted.
      return {
        ...state,
        accessToken: {
          ...state.accessToken,
          token: action.payload.token,
          tokenId: action.payload.tokenId,
          expiration: action.payload.expiration,
        },
      };
    case SET_PERSONAL_BOOKMARKS:
      return {
        ...state,
        personalBookmarks: action.payload,
        sPersonalBookmarks: action.payload,
      };
    case SET_SEARCH_BOOKMARKS:
      return { ...state, sPersonalBookmarks: action.payload };
    default:
      return state;
  }
};
